import styled from '@emotion/styled';
import { Theme } from '@emotion/react';

// 모바일 뷰 감지용 훅
export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < breakpoint : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

// Root Container
export const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

// Max Width Wrapper for centering content
export const MaxWidthWrapper = styled.div(({ theme }) => ({
  width: '100%',
  maxWidth: theme.breakpoints.xl || '1280px',
  margin: '0 auto',
  padding: `0 ${theme.spacing.md}`,
  display: 'flex',
  alignItems: 'center',
  height: '100%',
}));

// Header
export const Header = styled.header(({ theme }) => ({
  backgroundColor: `var(--my-card-background)`,
  borderBottom: `1px solid var(--theme-border)`,
  flexShrink: 0,
  paddingTop: theme.spacing.sm,
  paddingBottom: theme.spacing.sm,
  position: 'sticky',
  top: 0,
  zIndex: 1200,
  [`@media (max-width: ${theme.breakpoints.sm})`]: {
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
  },
}));

export const HeaderContentWrapper = styled(MaxWidthWrapper)({
  justifyContent: 'space-between',
  width: 'auto',
});

export const HeaderLeft = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing.sm,
  flexShrink: 1,
  minWidth: 0,
}));

export const HeaderRight = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing.sm,
  flexShrink: 0,
}));

// Buttons
export const StyledButton = styled.button(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
  border: `1px solid var(--theme-border)`,
  backgroundColor: `var(--my-card-background)`,
  borderRadius: theme.radius.sm,
  fontSize: theme.typography.fontSize.sm,
  fontWeight: theme.typography.fontWeight.medium,
  color: `var(--theme-text)`,
  cursor: 'pointer',
  transition: `background-color ${theme.transition.fast}`,
  gap: theme.spacing.xs,

  '&:hover:not(:disabled)': {
    backgroundColor: `var(--theme-paper-hover)`,
  },

  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
    color: `var(--theme-text-muted)`,
  },

  svg: {
    width: '16px',
    height: '16px',
  },

  [`@media (max-width: ${theme.breakpoints.sm})`]: {
    padding: theme.spacing.xs,
    'span': {
      display: 'none',
    },
    svg: {
      margin: 0,
    }
  },
}));

// Specific styles for Complete button to keep text visible
export const CompleteButton = styled(StyledButton)(({ theme }) => ({
  [`@media (max-width: ${theme.breakpoints.sm})`]: {
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    'span': {
      display: 'inline',
    },
  },
}));

export const StyledIconButton = styled.button(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing.sm,
  border: 'none',
  background: 'none',
  borderRadius: theme.radius.full,
  color: `var(--theme-text-secondary)`,
  cursor: 'pointer',
  transition: `background-color ${theme.transition.fast}`,

  '&:hover': {
    backgroundColor: `var(--theme-paper-hover)`,
    color: `var(--theme-text)`,
  },

  svg: {
    width: '20px',
    height: '20px',
  },
}));

// Main Content Area
export const MainContentArea = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
`;

export const MaxWidthContentWrapper = styled(MaxWidthWrapper)(({ theme }) => ({
  padding: 0,
  flexGrow: 1,
  alignItems: 'stretch',
}));

export const MainViewerContainer = styled.main`
  flex-grow: 1;
  background-color: var(--theme-background);
  position: relative;

  body.vscode-dark &, body.vscode-light & {
    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
  }
`;

// Sidebar Container
export const SidebarContainer = styled.aside<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  width: '300px',
  maxWidth: '80vw',
  height: '100%',
  borderLeft: `1px solid var(--theme-border)`,
  backgroundColor: `var(--my-card-background)`,
  overflowY: 'auto',
  transition: `transform ${theme.transition.normal}, opacity ${theme.transition.normal}, width ${theme.transition.normal}`,
  transform: isVisible ? 'translateX(0%)' : 'translateX(100%)',
  opacity: isVisible ? 1 : 0,
  flexShrink: 0,
  position: 'fixed',
  right: 0,
  top: 'calc(var(--header-height, 60px))',
  bottom: 0,
  zIndex: 1100,

  [`@media (min-width: ${theme.breakpoints.lg})`]: {
    position: 'relative',
    top: 'auto',
    bottom: 'auto',
    transform: 'none',
    zIndex: 'auto',
    width: isVisible ? '300px' : '0px',
    opacity: isVisible ? 1 : 0,
    borderLeft: isVisible ? `1px solid var(--theme-border)` : 'none',
    '> *': {
      display: isVisible ? 'block' : 'none',
    }
  },

  ...(!isVisible && {
    pointerEvents: 'none',
    [`@media (max-width: calc(${theme.breakpoints.lg} - 1px))`]: {
      visibility: 'hidden',
    }
  })
}));

// Lecture Title
export const LectureTitle = styled.h1(({ theme }) => ({
  fontSize: theme.typography.fontSize.lg,
  fontWeight: theme.typography.fontWeight.semibold,
  color: `var(--theme-text)`,
  margin: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  [`@media (max-width: ${theme.breakpoints.sm})`]: {
    fontSize: theme.typography.fontSize.base,
  },
}));